import React, { useState, useEffect, Suspense, lazy } from "react";
import Header from "./components/Layout/Header";
import ConnectionStatus from "./components/UI/ConnectionStatus";
import { NotificationToast } from "./components/UI/NotificationComponents";
import { useTranscriptionStore } from "./store";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, HashRouter } from "react-router-dom";
import NewCaseModal from "./components/UI/NewCaseModal";
import { checkAuthOnStartup, syncAuthWithBackend } from "./components/auth/authServices";
import useWebSocketStore, { useWebSocketConnection, useWebSocketStatus } from "./store/useWebSocketStore";
import api from "./config/axiosInstance";
import TranscriptionCreateModel from "./components/UI/TranscriptionCreateModel";

const HelpPage = lazy(() => import("./components/help/Help"));
const LoginScreen = lazy(() => import("./components/auth/Login"));
const CasesTable = lazy(() => import("./components/UI/CasesTable"));
const TranscriptView = lazy(() => import("./components/Transcript/TranscriptView"));
const RecordingControls = lazy(() => import("./components/Controls/RecordingControls"));
const BulkTranscription = lazy(() => import("./components/UI/BulkTranscription"));
const AdditionalDetailsModal = lazy(() => import("./components/UI/AdditionalDetaislModal"));
const LinkAudioModal = lazy(() => import("./components/UI/LinkAudioModal"));

const loadApi = () => import("./config/axiosInstance").then(module => module.default);

const PageLoader = ({ message = "Loading...", variant = "default" }) => {
  const variants = {
    default: "Initializing Radspeak AI",
    login: "Loading Login",
    cases: "Loading Cases",
    transcription: "Loading Transcription",
    session: "Loading Session"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center text-white">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-6"></div>
        <h2 className="text-xl font-semibold mb-2">{variants[variant] || message}</h2>
        <p className="text-white/70">Please wait...</p>
      </div>
    </div>
  );
};

const SuspenseWrapper = ({ children, fallbackVariant = "default" }) => (
  <Suspense fallback={<PageLoader variant={fallbackVariant} />}>
    {children}
  </Suspense>
);

const ProtectedRoute = ({ children }) => {
  const { auth } = useTranscriptionStore();

  if (!auth?.access_token) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const PublicRoute = ({ children }) => {
  const { auth } = useTranscriptionStore();

  if (auth?.access_token) {
    return <Navigate to="/cases" replace />;
  }

  return children;
};

const LoginPage = () => {
  const { setAuth } = useTranscriptionStore();
  const navigate = useNavigate();

  const onLogin = async (data) => {
    await setAuth(data);
    await syncAuthWithBackend()
    navigate('/cases');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SuspenseWrapper fallbackVariant="login">
        <LoginScreen onLogin={onLogin} />
      </SuspenseWrapper>
    </div>
  );
};

const CasesDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [allSessions, setAllSessions] = useState([]);
  const [linkedAudioModel, setShowLinkedAudioModel] = useState(false);
  const [additionalModal, setShowAdditionalModal] = useState(false);
  const [transcriptionCreateModel, setShowTranscriptionCreateModel] = useState(false);

  const [apiLoaded, setApiLoaded] = useState(false);

  const [search, setSearch] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [selectedAudioId, setSelectedAudioId] = useState(null);

  const [additionalNotes, setAdditionalNotes] = useState("");
  const [deadlineMinutes, setDeadlineMinutes] = useState(30);

  const { currentSession, setCurrentSession } = useTranscriptionStore();
  const navigate = useNavigate();

  // Lazy load API
  useEffect(() => {
    loadApi().then(api => {

      checkAuthOnStartup();

      setApiLoaded(true);
      window.apiInstance = api;
    }).catch(console.error);
  }, []);


  const linkedAudio = async () => {
    if (!apiLoaded || !window.apiInstance) return;

    try {
      await window.apiInstance.patch(`/api/v1/sessions/link/${currentSession?._id}`, {
        sourceSessionId: selectedCaseId,
        audioFileIds: [selectedAudioId]
      });
      setSelectedCaseId("");
      setSelectedAudioId("");
      setCurrentSession(currentSession?._id);
    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {
    if (currentSession) {
      navigate('/transcription');
    }
  }, [currentSession, navigate]);

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white flex flex-col overflow-hidden">
      <Header
        onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
        onShowSettings={() => setShowSettings(true)}
        sidebarCollapsed={sidebarCollapsed}
      />

      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 flex flex-col overflow-hidden relative">
          <SuspenseWrapper fallbackVariant="cases">
            <CasesTable />
          </SuspenseWrapper>
        </main>
      </div>

      <NotificationToast />
      <div className="fixed top-4 right-4 z-50">
        <ConnectionStatus />
      </div>

      {/* Lazy loaded modals */}
      {linkedAudioModel && (
        <SuspenseWrapper>
          <LinkAudioModal
            onClose={() => setShowLinkedAudioModel(false)}
            setShowLinkedAudioModel={setShowLinkedAudioModel}
            allSessions={allSessions}
            search={search}
            setSearch={setSearch}
            selectedCaseId={selectedCaseId}
            setSelectedCaseId={setSelectedCaseId}
            selectedAudioId={selectedAudioId}
            setSelectedAudioId={setSelectedAudioId}
            linkedAudio={linkedAudio}
          />
        </SuspenseWrapper>
      )}


      {additionalModal && (
        <SuspenseWrapper>
          <AdditionalDetailsModal
            onClose={() => setShowAdditionalModal(false)}
            setShowAdditionalModal={setShowAdditionalModal}
            additionalNotes={additionalNotes}
            setAdditionalNotes={setAdditionalNotes}
            deadlineMinutes={deadlineMinutes}
            setDeadlineMinutes={setDeadlineMinutes}
            currentSession={currentSession}
          />
        </SuspenseWrapper>
      )}


    </div>
  );
};

const BulkTranscriptionPage = () => {
  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white flex flex-col overflow-hidden">
      <Header />
      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 flex flex-col overflow-hidden relative">
          <SuspenseWrapper fallbackVariant="cases">
            <BulkTranscription />
          </SuspenseWrapper>
        </main>
      </div>
      <NotificationToast />
      <div className="fixed top-4 right-4 z-50">
        <ConnectionStatus />
      </div>
    </div>
  );
};

const TranscriptionPage = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [allSessions, setAllSessions] = useState([]);
  const [linkedAudioModel, setShowLinkedAudioModel] = useState(false);
  const [additionalModal, setShowAdditionalModal] = useState(false);

  const [transcriptionCreateModel, setShowTranscriptionCreateModel] = useState(false);

  const [editCaseModal, setEditCaseModal] = useState(false);
  const [editableCaseData, setEditableCaseData] = useState([]);
  const [apiLoaded, setApiLoaded] = useState(false);


  const { on, off } = useWebSocketStore()

  const [search, setSearch] = useState("");
  const [selectedCaseId, setSelectedCaseId] = useState(null);
  const [selectedAudioId, setSelectedAudioId] = useState(null);

  const [additionalNotes, setAdditionalNotes] = useState("");
  const [deadlineMinutes, setDeadlineMinutes] = useState(30);

  const { currentSession, setCurrentSession } = useTranscriptionStore();
  const navigate = useNavigate();


  useEffect(() => {
    const handler = (res) => {
      // setMedicalSuggestions(ssssssss)
      console.log(res);
    };

    on('suggestions', handler);

    return () => {
      off('suggestions', handler);
    };
  }, []);


  useEffect(() => {
    if (!window.apiInstance) {
      loadApi().then(api => {
        setApiLoaded(true);
        window.apiInstance = api;
      }).catch(console.error);
    } else {
      setApiLoaded(true);
    }
  }, []);


  const linkedAudio = async () => {
    if (!apiLoaded || !window.apiInstance) return;

    try {
      await window.apiInstance.patch(`/api/v1/sessions/link/${currentSession?._id}`, {
        sourceSessionId: selectedCaseId,
        audioFileIds: [selectedAudioId]
      });
      setSelectedCaseId("");
      setSelectedAudioId("");
      setCurrentSession(currentSession?._id);
    } catch (error) {
      console.error(error);
    }
  };


  useEffect(() => {
    if (!currentSession) {
      navigate('/cases');
    }
  }, [currentSession, navigate]);

  if (!currentSession) {
    return <PageLoader variant="session" />;
  }

  return (
    <div className="h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white flex flex-col overflow-hidden">
      <Header
        onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
        onShowSettings={() => setShowSettings(true)}
        sidebarCollapsed={sidebarCollapsed}
      />

      <div className="flex flex-1 overflow-hidden">
        <main className="flex-1 flex flex-col overflow-hidden relative">
          <div className="flex-1 overflow-hidden">
            <SuspenseWrapper fallbackVariant="transcription">
              <TranscriptView
                setShowAdditionalModal={setShowAdditionalModal}
                additionalModal={additionalModal}
                setShowLinkedAudioModel={setShowLinkedAudioModel}
                setShowTranscriptionCreateModel={setShowTranscriptionCreateModel}
                transcriptionCreateModel={transcriptionCreateModel}
                linkedAudioModel={linkedAudioModel}
                setEditCaseModal={setEditCaseModal}
                editCaseModal={editCaseModal}
                editableCaseData={editableCaseData}
                setEditableCaseData={setEditableCaseData}
              />
            </SuspenseWrapper>
          </div>

          <SuspenseWrapper>
            <RecordingControls setSidebarCollapsed={setSidebarCollapsed} />
          </SuspenseWrapper>
        </main>
      </div>

      <NotificationToast />
      <div className="fixed top-4 right-4 z-50">
        <ConnectionStatus />
      </div>

      {/* Lazy loaded modals */}
      {linkedAudioModel && (
        <SuspenseWrapper>
          <LinkAudioModal
            onClose={() => setShowLinkedAudioModel(false)}
            setShowLinkedAudioModel={setShowLinkedAudioModel}
            allSessions={allSessions}
            search={search}
            setSearch={setSearch}
            selectedCaseId={selectedCaseId}
            setSelectedCaseId={setSelectedCaseId}
            selectedAudioId={selectedAudioId}
            setSelectedAudioId={setSelectedAudioId}
            linkedAudio={linkedAudio}
          />
        </SuspenseWrapper>
      )}





      {transcriptionCreateModel && (
        <SuspenseWrapper>
          <TranscriptionCreateModel
            onClose={() => setShowTranscriptionCreateModel(false)}
            setShowTranscriptionCreateModel={setShowTranscriptionCreateModel}
            transcriptionCreateModel={transcriptionCreateModel}
            currentSession={currentSession}
          />
        </SuspenseWrapper>
      )}

      {additionalModal && (
        <SuspenseWrapper>
          <AdditionalDetailsModal
            onClose={() => setShowAdditionalModal(false)}
            setShowAdditionalModal={setShowAdditionalModal}
            additionalNotes={additionalNotes}
            setAdditionalNotes={setAdditionalNotes}
            deadlineMinutes={deadlineMinutes}
            setDeadlineMinutes={setDeadlineMinutes}
            currentSession={currentSession}
          />
        </SuspenseWrapper>
      )}

      {editCaseModal && (
        <SuspenseWrapper>
          <NewCaseModal
            onClose={() => setEditCaseModal(false)}
            initialData={currentSession}
          />
        </SuspenseWrapper>
      )}

    </div>
  );
};

const AppContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const {
    initializeApp,
    isInitialized,
    auth,
  } = useTranscriptionStore();



  useEffect(() => {
    const init = async () => {
      try {
        setIsLoading(true);
        await initializeApp();
      } catch (error) {
        console.error("Failed to initialize app:", error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [auth?.access_token]);

  if (!isInitialized || isLoading) {
    return <PageLoader variant="default" />;
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />

      <Route
        path="/help"
        element={
          <HelpPage />
        }
      />


      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <CasesDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/bulk-transcription"
        element={
          <ProtectedRoute>
            <BulkTranscriptionPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/transcription"
        element={
          <ProtectedRoute>
            <TranscriptionPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/"
        element={
          auth?.access_token ?
            <Navigate to="/cases" replace /> :
            <Navigate to="/login" replace />
        }
      />

      <Route
        path="*"
        element={
          auth?.access_token ?
            <Navigate to="/cases" replace /> :
            <Navigate to="/login" replace />
        }
      />
    </Routes>
  );
};

const RadspeakAI = () => {
  const { connect, disconnect, reconnect, isReady } = useWebSocketStore();
  const { connected, connecting, error, reconnectCount } = useWebSocketStore();
  const { auth } = useTranscriptionStore();

  useEffect(() => {
    if (auth?.access_token && !connected) { }
    // connect()
  }, [auth])

  // React to Python's inactivity-triggered force logout
  useEffect(() => {
    const handlePyEvent = (e) => {
      const { method, data } = e.detail || {};
      if (method === 'force_logout') {
        console.log('[force_logout] received from Python:', data?.reason);
        localStorage.clear();
        window.location.reload(true);
      }
    };

    window.addEventListener('py-event', handlePyEvent);
    return () => window.removeEventListener('py-event', handlePyEvent);
  }, []);


  // Report user activity to the Python idle monitor (throttled) so it can
  // auto-logout after 2hrs of true inactivity. Only pings while logged in.
  useEffect(() => {
    if (!auth?.access_token) return;

    let lastPing = 0;
    const THROTTLE_MS = 30000; // once every 30s max

    const reportActivity = () => {
      const now = Date.now();
      if (now - lastPing < THROTTLE_MS) return;
      lastPing = now;

      if (window.pywebview?.api?.record_activity) {
        window.pywebview.api.record_activity().catch((err) => {
          console.error('Failed to report activity:', err);
        });
      }
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((evt) => document.addEventListener(evt, reportActivity, { passive: true }));

    return () => {
      events.forEach((evt) => document.removeEventListener(evt, reportActivity));
    };
  }, [auth?.access_token]);

  const ConnectionControl = () => {

    return (
      <div className="bg-white">
        <h3>WebSocket Control</h3>
        <p>Status: {connected ? '🟢 Connected' : connecting ? '🟡 Connecting...' : '🔴 Disconnected'}</p>
        {error && <p>❌ Error: {error}</p>}
        {reconnectCount > 0 && <p>🔄 Reconnect attempts: {reconnectCount}</p>}

        <button onClick={connect} disabled={connected || connecting}>
          Connect
        </button>
        <button onClick={disconnect} disabled={!connected && !connecting}>
          Disconnect
        </button>
        <button onClick={reconnect}>
          Reconnect
        </button>
      </div>
    );
  };


  return (
    <HashRouter>
      <Suspense fallback={<PageLoader variant="default" />}>
        {/* <ConnectionControl /> */}
        <AppContent />
      </Suspense>
    </HashRouter>
  );
};

export default RadspeakAI;